import { transparentize } from 'polished';
import { css } from 'styled-components';

import { TabItem } from 'components/common/Tab';

import theme from 'styles/token/color';
import { typography } from 'styles/token/typography';

/**
 * 버튼 스타일
 * @param {string} variant - 버튼 타입
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
export const getVariantButtonStyle = (variant) => {
  switch (variant) {
    case 'primary':
      return css`
        color: ${({ theme }) => theme.color.white};
        background: ${({ theme }) => theme.color.primary};
      `;
    case 'primary-outline':
      return css`
        color: ${({ theme }) => theme.color.primary};
        background: ${({ theme }) => theme.color.white};
        border: 1px solid ${({ theme }) => theme.color.primary};
      `;
    case 'primary-dark':
      return css`
        color: ${({ theme }) => theme.color.white};
        background: ${({ theme }) => theme.color.gray820};
      `;
    case 'primary-dark-outline':
      return css`
        border: 1px solid ${({ theme }) => theme.color.primaryDark};
        color: ${({ theme }) => theme.color.primaryDark};
        background: ${({ theme }) => theme.color.white};
      `;
    case 'gray-outline':
      return css`
        background: ${({ theme }) => theme.color.white};
        border: 1px solid ${({ theme }) => theme.color.gray200};
        color: ${({ theme }) => theme.color.primaryDark};
      `;
    case 'dark':
      return css`
        color: ${({ theme }) => theme.color.white};
        background: ${({ theme }) => theme.color.gray700};
        border: none;
      `;
    case 'dark-outline':
      return css`
        color: ${({ theme }) => theme.color.gray700};
        background: ${({ theme }) => theme.color.white};
        border: 1px solid ${({ theme }) => theme.color.gray200};
      `;
    case 'primary-gray-outline':
      return css`
        color: ${({ theme }) => theme.color.primary};
        background: ${({ theme }) => theme.color.white};
        border: 1px solid ${({ theme }) => theme.color.gray200};
      `;
    case 'disabled':
      return css`
        color: ${({ theme }) => theme.color.white};
        background: ${({ theme }) => theme.color.gray300};
        border: none;
        cursor: not-allowed;
      `;
    case 'mw-sample':
      return css`
        // ⭐️ 기존 스타일
        /* border: 1px solid transparent;
        background: ${({ theme }) => theme.color.primaryDark} !important;
        color: ${({ theme }) => theme.color.white}; */
        // ⭐️ 변경 스타일
        border: 1px solid ${({ theme }) => theme.color.gray100};
        background: ${({ theme }) => theme.color.white} !important;
      `;
    case 'mw-sample-outline':
      // ⭐️ 변경 스타일
      return css`
        color: ${({ theme }) => theme.color.gray600};
        background: ${({ theme }) => theme.color.gray80};
        border: 1px solid ${({ theme }) => theme.color.gray100};
      `;
    default:
      return css``;
  }
};

/**
 * 태그 스타일
 * @param {string} variant - 태그 타입
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
export const getVariantTagStyle = (variant) => {
  // 투명도 기반 컬러 variant 목록
  const transparentColorKeys = [
    'pink',
    'red',
    'orange',
    'yellow',
    'green',
    'purple',
  ];

  const matchedKey = transparentColorKeys.find((key) => variant?.includes(key));

  if (matchedKey) {
    const alpha = 0.9;

    const getColor = (themeColor) =>
      themeColor[variant] ||
      themeColor[matchedKey] ||
      themeColor[`${variant}500`] ||
      themeColor[`${matchedKey}500`] ||
      themeColor[`${matchedKey}400`];

    return css`
      background: ${({ theme }) => {
        const targetColor = getColor(theme.color);
        return typeof targetColor === 'string'
          ? transparentize(alpha, targetColor)
          : theme.color.gray90;
      }};
      color: ${({ theme }) => getColor(theme.color) || theme.color.gray700};
    `;
  }

  switch (variant) {
    case 'primary':
      return css`
        background: ${({ theme }) => transparentize(0.9, theme.color.primary)};
        color: ${({ theme }) => theme.color.primary};
      `;
    case 'blueGradient':
      return css`
        color: ${({ theme }) => theme.color.white};
        background: linear-gradient(
          120deg,
          ${({ theme }) => theme.color.blue300} 9%,
          ${({ theme }) => theme.color.primary}
        );
      `;
    case 'gray':
      return css`
        background: ${({ theme }) => theme.color.gray90};
        color: ${({ theme }) => theme.color.gray700};
      `;
    case 'secondary':
    default:
      return css`
        background: ${({ theme }) => theme.color.white};
        color: ${({ theme }) => theme.color.gray500};
        border: 1px solid ${({ theme }) => theme.color.gray200};
      `;
  }
};

/**
 * 텍스트 색상
 * @param {string} variant - 텍스트 타입
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
export const getVariantTextStyle = (variant) => {
  return theme.color[variant];
};

/**
 * 색상 스타일
 * @param {string} variant - 색상 타입
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
export const getVariantStyle = (variant) => {
  switch (variant) {
    case 'primary':
      return css`
        color: ${({ theme }) => theme.color.primary};
      `;
    case 'primaryDark':
      return css`
        color: ${({ theme }) => theme.color.primaryDark};
      `;
    case 'warning':
      return css`
        color: ${({ theme }) => theme.color.yellow900};
      `;
    case 'error':
      return css`
        color: ${({ theme }) => theme.color.red700};
      `;
    case 'success':
      return css`
        color: ${({ theme }) => theme.color.green500};
      `;
    case 'secondary':
      return css`
        color: ${({ theme }) => theme.color.gray400};
      `;
    case 'REQUEST':
    case 'CANCELLATION':
    case 'PREPARING':
    case 'ACCEPTANCE':
    case 'REJECTION':
    case 'active':
    case 'paused':
    case 'stopped':
    case 'connected':
      const styleBadgeColor = {
        // enterprise 신청 상태 아이콘
        REQUEST: 'green500',
        CANCELLATION: 'yellow100',
        PREPARING: 'purple500',
        ACCEPTANCE: 'primary',
        REJECTION: 'red700',

        active: 'primary',
        paused: 'yellow100',
        stopped: 'red700',
        connected: 'gray600',
      };

      const resolvedColor = styleBadgeColor[variant] || variant;
      const colorValue = theme.color[resolvedColor] || theme.color.gray400;
      return css`
        color: ${colorValue};
      `;

    default:
      return css`
        color: ${({ theme }) => theme.color.gray900};
      `;
  }
};

/**
 * 탭 스타일
 * @param {string} variant - 탭 타입
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
export const getVariantTabStyle = (variant) => {
  switch (variant) {
    case 'boxed':
      return css`
        gap: 2px;
        height: 40px;

        ${TabItem} {
          font-size: 15px;
          width: 100px;
          border: 1px solid ${theme.color.blueGray100};
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 0;
          background: ${theme.color.gray90};
          border-radius: 6px 6px 0 0;

          &.active {
            color: ${theme.color.primary};
            background: ${theme.color.white};
            border-bottom: 0;

            &:after {
              content: '';
              display: inline-block;
              position: absolute;
              bottom: -1px;
              width: 100%;
              height: 1px;
              background: ${theme.color.white};
            }
          }

          @media screen and (max-width: 768px) {
            width: 65px;
            font-size: 13px;
          }
        }
      `;
    case 'underlined':
      return css`
        width: 100%;
        flex-shrink: 0;

        ${TabItem} {
          flex: 1;
          padding: 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: ${theme.color.gray500};

          .tab_count {
            margin-left: 4px;

            color: ${theme.color.gray400};
          }

          &.active {
            color: ${theme.color.primary};

            .tab_count {
              color: ${theme.color.primary};
              opacity: 0.7;
            }

            &:after {
              content: '';
              position: absolute;
              bottom: -1px;
              left: 0;
              right: 0;
              height: 2px;
              background: ${theme.color.primary};
            }
          }

          &.disabled {
            cursor: not-allowed;
            opacity: 0.4;
            background: ${theme.color.gray180};

            &:hover {
              background: ${theme.color.gray180};
            }
          }

          &:hover:not(.active):not(.disabled) {
            background: ${theme.color.gray70};
          }
        }
      `;
    case 'full':
      return css`
        width: 100%;
        flex-shrink: 0;

        ${TabItem} {
          flex: 1;
          padding: 10px 0;
          color: ${theme.color.gray500};
          ${typography.labelLarge}

          &.active {
            color: ${theme.color.primary};
          }
          &.disabled {
            cursor: not-allowed;
            opacity: 0.4;
            background: ${theme.color.gray180};

            &:hover {
              background: ${theme.color.gray180};
            }
          }
        }
      `;
    default:
      return css``;
  }
};

/**
 * 토스트 아이콘의 색상을 반환하는 함수
 * @param {string} severity - 토스트의 심각도
 * @returns {string} 토스트 아이콘의 색상
 */
export const getVariantToastStyle = (severity) => {
  const severityMap = {
    info: theme.color.primary,
    success: theme.color.primary,
    error: theme.color.red600,
    default: theme.color.gray300,
  };
  return severityMap[severity];
};

/**
 * @desc 메시지 박스 색상을 변환하는 함수
 * @param {*} variant - 스타일 타입 (primary, default 등)
 * @returns {String} CSS 스타일
 */
export const getVariantMessageStyle = (variant) => {
  const variantStyles = {
    primary: css`
      border: 1px solid ${({ theme }) => theme.color.blueGray100};
      background: ${({ theme }) => theme.color.primaryBg};

      .title {
        color: ${({ theme }) => theme.color.primaryDark};
        .info_icon {
          color: ${({ theme }) => theme.color.primary};
        }
      }
      li {
        color: ${({ theme }) => theme.color.primaryDark};
      }
    `,
    info: css`
      color: ${({ theme }) => theme.color.infoText};
      border: 1px solid ${({ theme }) => theme.color.infoBorder};
      background: ${({ theme }) => theme.color.infoBg};
    `,
  };

  return variantStyles[variant] || variantStyles.default;
};

/**
 * @desc 엑셀 다운로드 시 스타일을 반환하는 함수
 * @returns {Object} - 엑셀 스타일
 */
export const getVariantExcelStyle = () => {
  const baseColor = theme.color.primaryDark.replace('#', ''); // 기준 색상
  const whiteColor = theme.color.white.replace('#', ''); // 흰색

  // 공통 스타일
  const commonStyle = {
    alignment: {
      horizontal: 'left', // 모든 텍스트 왼쪽 정렬
      vertical: 'center', // 수직 가운데 정렬
    },
    font: {
      bold: false, // 기본 폰트 굵기
      color: { rgb: baseColor }, // 글꼴 색상 (기준 색상)
    },
    fill: {
      fgColor: { rgb: whiteColor }, // 기본 배경 흰색
    },
    border: {
      bottom: {
        style: 'thin', // 선 스타일 (thin, medium, thick 등)
        color: { rgb: baseColor }, // 테이블 행 아래쪽 테두리만 기준 색상
      },
    },
  };

  const headerStyle = {
    alignment: commonStyle.alignment,
    font: {
      bold: true, // 헤더는 굵은 글씨
      color: { rgb: whiteColor }, // 흰색 폰트
    },
    fill: {
      fgColor: { rgb: baseColor }, // 헤더 배경 기준 색상
    },
  };

  const centerAlignStyle = {
    alignment: {
      horizontal: 'center', // 모든 텍스트 가운데 정렬
      vertical: 'center', // 수직 가운데 정렬
    },
  };

  return { common: commonStyle, header: headerStyle, center: centerAlignStyle };
};
